import { useCallback, useEffect, useRef, useState } from "react";
import { QUICK_STUDY_PATHS, quickStep, withoutQuick } from "./quickStudyRun";
import { electricalSaveFailure } from "./config/electricalSaveSnapshot";

const MAX_RETRIES = 3;

// Own the timers for one project/visit. Leaving the step, switching project or
// unmounting invalidates both scheduled retries and late save responses.
export function useQuickDesignStudy({ active, projectId, runAutoDesign, navigate, onNotice, isGreek }) {
  const latest = useRef(null);
  latest.current = { runAutoDesign, navigate, onNotice, isGreek, active, projectId };
  const sessionRef = useRef(null);
  const [request, setRequest] = useState(0);
  const current = useCallback(session => Boolean(session?.active && sessionRef.current === session &&
    latest.current.active && latest.current.projectId === session.projectId && quickStep(window.location) === "design"), []);
  const stop = useCallback((session, notice) => {
    if (!current(session)) return;
    session.active = false;
    clearTimeout(session.timer);
    if (notice) latest.current.onNotice(notice);
    latest.current.navigate(withoutQuick(window.location), { replace: true });
  }, [current]);

  useEffect(() => {
    if (!active || !projectId) return undefined;
    const session = { active: true, ready: false, projectId, retries: 0, timer: null };
    sessionRef.current = session;
    session.timer = setTimeout(async () => {
      if (!current(session)) return;
      let ok;
      try {
        ok = await latest.current.runAutoDesign(); // This callback displays its own design errors.
      } catch (error) {
        stop(session, { severity: "warning", message: electricalSaveFailure(error, latest.current.isGreek).message });
        return;
      }
      if (!current(session)) return;
      if (!ok) { stop(session); return; }
      session.ready = true;
      setRequest(value => value + 1);
    }, 3000);
    return () => { session.active = false; clearTimeout(session.timer); };
  }, [active, projectId, current, stop]);

  const owner = sessionRef.current;
  const onSaved = useCallback(result => {
    if (!current(owner) || !owner.ready) return;
    const tr = (el, en) => latest.current.isGreek ? el : en;
    const status = typeof result === "string" ? result : result?.status;
    // A duplicate completion cannot schedule a second concurrent attempt.
    clearTimeout(owner.timer);
    if (status === "saved") {
      owner.active = false;
      latest.current.navigate(QUICK_STUDY_PATHS.run);
      return;
    }
    const temporary = status === "busy" || (status === "error" && result?.retryable === true);
    const delay = Math.max(3000 * 2 ** owner.retries, Number(result?.retryAfterMs) || 0);
    if (temporary && owner.retries < MAX_RETRIES && delay <= 60000) {
      owner.retries += 1;
      const seconds = Math.ceil(delay / 1000);
      latest.current.onNotice({ severity: "info", message: tr(
        `Η γρήγορη μελέτη περιμένει προσωρινά. Νέα προσπάθεια αποθήκευσης σε ${seconds} δευτερόλεπτα (${owner.retries}/${MAX_RETRIES}). Δεν χρειάζεται ενέργεια από εσάς.`,
        `Quick study is temporarily waiting. Saving will be retried in ${seconds} seconds (${owner.retries}/${MAX_RETRIES}). No action is needed.`) });
      owner.timer = setTimeout(() => {
        if (current(owner)) setRequest(value => value + 1);
      }, delay);
      return;
    }
    const reason = status === "changed" ? tr(
      "Το σχέδιο άλλαξε κατά τον έλεγχο. Ελέγξτε τις νέες ρυθμίσεις και πατήστε «Αποθήκευση σχεδίου».",
      "The design changed during the check. Review the new settings and click Save design.")
      : status === "invalid" ? tr(
        "Διορθώστε τα ηλεκτρολογικά σφάλματα που εμφανίζονται παρακάτω και πατήστε «Αποθήκευση σχεδίου».",
        "Correct the electrical errors shown below and click Save design.")
      : temporary ? tr(
        "Η αποθήκευση δεν ολοκληρώθηκε αυτόματα. Περιμένετε λίγο, ελέγξτε τη σύνδεσή σας και πατήστε «Αποθήκευση σχεδίου» για νέα προσπάθεια.",
        "Saving could not complete automatically. Wait a little, check your connection and click Save design to try again.")
      : result?.message || tr(
        "Ελέγξτε το μήνυμα σφάλματος και πατήστε «Αποθήκευση σχεδίου» όταν διορθωθεί η αιτία.",
        "Review the error message and click Save design once the cause is resolved.");
    stop(owner, { severity: "warning", message: tr("Η γρήγορη μελέτη σταμάτησε. ", "Quick study stopped. ") + reason });
  }, [owner, current, stop]);

  return { saveRequest: active && owner?.projectId === projectId && owner?.active && owner?.ready ? request : 0, onSaved };
}
